import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { fetchAndCachePublicData, getCachedPublicData } from "../utils/publicDataCache";

const API = "/api";
const STATE_CACHE_KEY = "public-states";
const DISTRICT_CACHE_KEY = "public-districts";
const FRANCHISE_CACHE_KEY = "public-approved-franchises";
const COURSE_CACHE_KEY = "public-course-catalog";

const shellStyle = {
  minHeight: "100vh",
  background: "linear-gradient(180deg, #fff7ed 0%, #fff 42%, #f8fafc 100%)",
  padding: "32px 0 60px",
};

const heroCardStyle = {
  background: "linear-gradient(135deg, rgba(219,234,254,0.9) 0%, rgba(255,247,237,0.98) 100%)",
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: "30px",
  padding: "28px",
  boxShadow: "0 24px 56px rgba(15, 23, 42, 0.08)",
  marginBottom: "24px",
};

const panelStyle = {
  background: "rgba(255,255,255,0.96)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: "28px",
  padding: "24px",
  boxShadow: "0 24px 50px rgba(15, 23, 42, 0.08)",
};

const statGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "14px",
  marginTop: "18px",
};

const statCardStyle = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.95) 100%)",
  border: "1px solid rgba(148, 163, 184, 0.16)",
  borderRadius: "20px",
  padding: "16px",
};

const formGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
};

const wideCardStyle = {
  ...statCardStyle,
  gridColumn: "1 / -1",
};

function getTodayInputValue() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const parts = formatter.formatToParts(new Date());
  const year = parts.find((item) => item.type === "year")?.value || "";
  const month = parts.find((item) => item.type === "month")?.value || "";
  const day = parts.find((item) => item.type === "day")?.value || "";

  return `${year}-${month}-${day}`;
}

function calculateCompletionDate(admissionDate, courseId, courses) {
  if (!admissionDate || !courseId) return "";
  const course = courses.find((item) => String(item.id) === String(courseId));
  const duration = String(course?.duration || "").toLowerCase();
  const matches = [...duration.matchAll(/(\d+(?:\.\d+)?)\s*(year|years|yr|yrs|month|months|mon|mons|week|weeks|day|days)/g)];
  if (!matches.length) return "";

  const date = new Date(`${admissionDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";

  matches.forEach((match) => {
    const amount = Number(match[1]);
    const unit = match[2];
    if (unit.startsWith("year") || unit.startsWith("yr")) {
      date.setMonth(date.getMonth() + Math.round(amount * 12));
    } else if (unit.startsWith("month") || unit.startsWith("mon")) {
      date.setMonth(date.getMonth() + Math.round(amount));
    } else if (unit.startsWith("week")) {
      date.setDate(date.getDate() + Math.round(amount * 7));
    } else if (unit.startsWith("day")) {
      date.setDate(date.getDate() + Math.round(amount));
    }
  });

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function StudentRegistration() {
  const today = useMemo(() => getTodayInputValue(), []);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [form, setForm] = useState({
    student_name: "",
    phone: "",
    email: "",
    dob: "",
    father_name: "",
    admission_date: today,
    course_completion_date: "",
    guardian_name: "",
    address: "",
    course_id: "",
    state_id: "",
    district_id: "",
    franchise_id: "",
  });
  const [files, setFiles] = useState({
    photo: null,
    signature: null,
    id_proof: null,
    academic_document: null,
  });

  useEffect(() => {
    let active = true;

    const cachedStates = getCachedPublicData(STATE_CACHE_KEY);
    const cachedDistricts = getCachedPublicData(DISTRICT_CACHE_KEY);
    const cachedFranchises = getCachedPublicData(FRANCHISE_CACHE_KEY);
    const cachedCourses = getCachedPublicData(COURSE_CACHE_KEY);

    if (cachedStates && active) {
      setStates(cachedStates.data);
    }

    if (cachedDistricts && active) {
      setDistricts(cachedDistricts.data);
    }

    if (cachedFranchises && active) {
      setFranchises(cachedFranchises.data);
    }

    if (cachedCourses && active) {
      setCourses(cachedCourses.data);
    }

    if ((cachedStates || cachedDistricts || cachedFranchises || cachedCourses) && active) {
      setLoading(false);
    }

    Promise.all([
      fetchAndCachePublicData(STATE_CACHE_KEY, "/states"),
      fetchAndCachePublicData(DISTRICT_CACHE_KEY, "/districts"),
      fetchAndCachePublicData(FRANCHISE_CACHE_KEY, "/approved-franchises"),
      fetchAndCachePublicData(COURSE_CACHE_KEY, "/courses-public"),
    ])
      .then(([stateRows, districtRows, franchiseRows, courseRows]) => {
        if (!active) return;
        setStates(stateRows);
        setDistricts(districtRows);
        setFranchises(franchiseRows);
        setCourses(courseRows);
      })
      .catch(() => {
        if (!active) return;
        if (!cachedStates) setStates([]);
        if (!cachedDistricts) setDistricts([]);
        if (!cachedFranchises) setFranchises([]);
        if (!cachedCourses) setCourses([]);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const filteredDistricts = useMemo(
    () =>
      districts.filter(
        (item) => String(item.state_id || "") === String(form.state_id || "")
      ),
    [districts, form.state_id]
  );

  const filteredFranchises = useMemo(
    () =>
      franchises.filter(
        (item) => String(item.district_id || "") === String(form.district_id || "")
      ),
    [franchises, form.district_id]
  );

  const selectedDistrict = useMemo(
    () => filteredDistricts.find((item) => String(item.id) === String(form.district_id || "")),
    [filteredDistricts, form.district_id]
  );

  const selectedFranchise = useMemo(
    () => filteredFranchises.find((item) => String(item.id) === String(form.franchise_id || "")),
    [filteredFranchises, form.franchise_id]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => {
      const next = {
        ...prev,
        [name]: value,
        ...(name === "state_id" ? { district_id: "", franchise_id: "" } : {}),
        ...(name === "district_id" ? { franchise_id: "" } : {}),
      };

      if (name === "course_id" || name === "admission_date") {
        next.course_completion_date = calculateCompletionDate(next.admission_date, next.course_id, courses);
      }

      return next;
    });
  };

  const handleFileChange = (event) => {
    const { name, files: selectedFiles } = event.target;
    setFiles((prev) => ({ ...prev, [name]: selectedFiles?.[0] || null }));
  };

  const resetForm = () => {
    setForm({
      student_name: "",
      phone: "",
      email: "",
      dob: "",
      father_name: "",
      admission_date: today,
      course_completion_date: "",
      guardian_name: "",
      address: "",
      course_id: "",
      state_id: "",
      district_id: "",
      franchise_id: "",
    });
    setFiles({
      photo: null,
      signature: null,
      id_proof: null,
      academic_document: null,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const requiredFields = [
      ["state_id", "State"],
      ["district_id", "District"],
      ["franchise_id", "Franchise / Study Centre"],
      ["student_name", "Student name"],
      ["phone", "Phone"],
      ["email", "Email"],
      ["dob", "Date of birth"],
      ["father_name", "Father name"],
      ["course_id", "Course"],
      ["admission_date", "Admission date"],
      ["course_completion_date", "Course completion"],
      ["guardian_name", "Guardian name"],
      ["address", "Address"],
    ];
    const missingField = requiredFields.find(([key]) => !String(form[key] || "").trim());

    if (missingField) {
      setMessage({ type: "error", text: `${missingField[1]} is required.` });
      return;
    }

    const missingFile = Object.entries(files).find(([, value]) => !value);
    if (missingFile) {
      setMessage({ type: "error", text: `${missingFile[0].replaceAll("_", " ")} is required.` });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: "", text: "" });

      const payload = new FormData();
      Object.entries({
        ...form,
        admission_date: form.admission_date || today,
      }).forEach(([key, value]) => {
        if (!["district_id", "franchise_id"].includes(key)) {
          payload.append(key, value || "");
        }
      });

      Object.entries(files).forEach(([key, value]) => {
        if (value) {
          payload.append(key, value);
        }
      });

      const response = await axios.post(
        `${API}/franchise-portal/${form.franchise_id}/applications`,
        payload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setMessage({
        type: "success",
        text: response.data?.application_no
          ? `Registration submitted successfully. Application No: ${response.data.application_no}`
          : response.data?.message || "Registration submitted successfully.",
      });
      resetForm();
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Student registration could not be submitted right now.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={shellStyle}>
      <div className="container">
        <div style={heroCardStyle}>
          <div className="row g-4 align-items-center">
            <div className="col-lg-7">
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  borderRadius: "999px",
                  background: "rgba(255,255,255,0.74)",
                  color: "#7c2d12",
                  fontWeight: 700,
                  fontSize: "13px",
                }}
              >
                Student Admission Form
              </span>
              <h1 style={{ margin: "16px 0 12px", color: "#0f172a", fontSize: "clamp(2rem, 4vw, 3.35rem)" }}>
                Student registration now goes directly to the selected study centre.
              </h1>
              <p style={{ margin: 0, color: "#475569", lineHeight: 1.8, fontSize: "15px" }}>
                District select korle oi district-er franchise list show hobe, franchise select korle application
                ta automatic oi centre-r Applied Student list-e chole jabe.
              </p>
            </div>
            <div className="col-lg-5">
              <div style={panelStyle}>
                <div style={{ color: "#7c2d12", fontWeight: 800, letterSpacing: "0.08em", fontSize: "12px", textTransform: "uppercase" }}>
                  Admission Snapshot
                </div>
                <div style={statGridStyle}>
                  <div style={statCardStyle}>
                    <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700, marginBottom: "6px" }}>Admission Date</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{today}</div>
                  </div>
                  <div style={statCardStyle}>
                    <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700, marginBottom: "6px" }}>District Count</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{districts.length}</div>
                  </div>
                  <div style={statCardStyle}>
                    <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700, marginBottom: "6px" }}>Active Centres</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{franchises.length}</div>
                  </div>
                  <div style={statCardStyle}>
                    <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 700, marginBottom: "6px" }}>Course Options</div>
                    <div style={{ color: "#0f172a", fontWeight: 700 }}>{courses.length}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={panelStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
            <div>
              <h2 style={{ margin: "0 0 6px", color: "#0f172a" }}>New Student Registration</h2>
              <p style={{ margin: 0, color: "#64748b", lineHeight: 1.7 }}>
                Franchise panel-er new application form-er same workflow maintain kore ekhane public registration ready করা হয়েছে।
              </p>
            </div>
            <div
              style={{
                padding: "10px 14px",
                borderRadius: "16px",
                background: "rgba(255,247,237,0.8)",
                border: "1px solid rgba(251, 191, 36, 0.26)",
                color: "#9a3412",
                fontWeight: 700,
                fontSize: "13px",
              }}
            >
              Admission date auto-set to today
            </div>
          </div>

          {message.text ? (
            <div
              style={{
                marginBottom: "18px",
                padding: "14px 16px",
                borderRadius: "18px",
                border: `1px solid ${message.type === "success" ? "#86efac" : "#fecaca"}`,
                background: message.type === "success" ? "#f0fdf4" : "#fef2f2",
                color: message.type === "success" ? "#166534" : "#b91c1c",
                fontWeight: 600,
              }}
            >
              {message.text}
            </div>
          ) : null}

          {loading ? (
            <div className="text-center text-muted py-5">Loading registration data...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={formGridStyle}>
                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">State</label>
                  <select className="form-select" name="state_id" value={form.state_id} onChange={handleChange} required>
                    <option value="">Select State</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">District</label>
                  <select
                    className="form-select"
                    name="district_id"
                    value={form.district_id}
                    onChange={handleChange}
                    disabled={!form.state_id}
                    required
                  >
                    <option value="">{form.state_id ? "Select District" : "Select state first"}</option>
                    {filteredDistricts.map((district) => (
                      <option key={district.id} value={district.id}>
                        {district.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Franchise / Study Centre</label>
                  <select
                    className="form-select"
                    name="franchise_id"
                    value={form.franchise_id}
                    onChange={handleChange}
                    disabled={!form.district_id}
                    required
                  >
                    <option value="">{form.district_id ? "Select Franchise" : "Select district first"}</option>
                    {filteredFranchises.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.centre_name || item.login_id}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Student Name</label>
                  <input className="form-control" name="student_name" value={form.student_name} onChange={handleChange} placeholder="Student Name" required />
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Phone</label>
                  <input className="form-control" name="phone" value={form.phone} onChange={handleChange} placeholder="Phone Number" required />
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Email</label>
                  <input className="form-control" type="email" name="email" value={form.email} onChange={handleChange} placeholder="Email Address" required />
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Date of Birth</label>
                  <input className="form-control" type="date" name="dob" value={form.dob} onChange={handleChange} required />
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Father Name</label>
                  <input className="form-control" name="father_name" value={form.father_name} onChange={handleChange} placeholder="Father Name" required />
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Course</label>
                  <select className="form-select" name="course_id" value={form.course_id} onChange={handleChange} required>
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Admission Date</label>
                  <input className="form-control" type="date" name="admission_date" value={form.admission_date} readOnly required />
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Course Completion</label>
                  <input className="form-control" type="date" name="course_completion_date" value={form.course_completion_date} readOnly required />
                </div>

                <div style={statCardStyle}>
                  <label className="form-label fw-semibold">Guardian Name</label>
                  <input className="form-control" name="guardian_name" value={form.guardian_name} onChange={handleChange} placeholder="Guardian Name" required />
                </div>

                <div style={wideCardStyle}>
                  <label className="form-label fw-semibold">Address</label>
                  <textarea className="form-control" rows="4" name="address" value={form.address} onChange={handleChange} placeholder="Full address" required></textarea>
                </div>

                {[
                  ["photo", "Photo"],
                  ["signature", "Signature"],
                  ["id_proof", "ID Proof"],
                  ["academic_document", "Academic Document"],
                ].map(([key, label]) => (
                  <div key={key} style={statCardStyle}>
                    <label className="form-label fw-semibold">{label}</label>
                    <input className="form-control" type="file" name={key} onChange={handleFileChange} required />
                  </div>
                ))}

                <div style={wideCardStyle}>
                  <div className="row g-3 align-items-center">
                    <div className="col-lg-8">
                      <div style={{ color: "#7c2d12", fontWeight: 800, marginBottom: "6px" }}>Selected Centre Preview</div>
                      <div style={{ color: "#475569", lineHeight: 1.7 }}>
                        State: <strong>{states.find((item) => String(item.id) === String(form.state_id || ""))?.name || "-"}</strong>
                        {" | "}
                        District: <strong>{selectedDistrict?.name || "-"}</strong>
                        {" | "}
                        Centre: <strong>{selectedFranchise?.centre_name || selectedFranchise?.login_id || "-"}</strong>
                        {" | "}
                        Centre Code: <strong>{selectedFranchise?.centre_code || selectedFranchise?.login_id || "-"}</strong>
                      </div>
                      {!filteredFranchises.length && form.district_id ? (
                        <div style={{ color: "#b91c1c", marginTop: "8px", fontWeight: 600 }}>
                          This district has no active franchise right now.
                        </div>
                      ) : null}
                    </div>
                    <div className="col-lg-4">
                      <button type="submit" className="btn btn-success w-100 py-3 fw-semibold" disabled={saving}>
                        {saving ? "Submitting..." : "Submit Registration"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentRegistration;
